// Customers Settings Tab — manage customers, entities, and users (200 lines max)
import { useState } from 'react'
import { Building2, Plus, Edit2, Trash2, Users as UsersIcon, Server } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useCustomers, useCustomerEntities, useCustomerUsers } from '@/hooks/useCustomers'
import { CustomerDialog } from './CustomerDialog'
import { EntityDialog } from './EntityDialog'
import { CustomerUserDialog } from './CustomerUserDialog'
import { CustomerDocumentsSection } from './CustomerDocumentsSection'

export function CustomersSettingsTab() {
  const { customers, isLoading, deleteCustomer } = useCustomers()
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null)
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false)
  const [entityDialogOpen, setEntityDialogOpen] = useState(false)
  const [userDialogOpen, setUserDialogOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<string | null>(null)

  const { entities } = useCustomerEntities(selectedCustomerId)
  const { users } = useCustomerUsers(selectedCustomerId)

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId)

  const handleDeleteCustomer = async (id: string) => {
    if (!confirm('Delete this customer and all associated entities and users?')) return
    try {
      await deleteCustomer(id)
      if (selectedCustomerId === id) setSelectedCustomerId(null)
    } catch (err) {
      alert('Failed to delete customer: ' + (err as Error).message)
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Customers List */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Customers
              </CardTitle>
              <CardDescription className="mt-1">
                {customers.length} customer{customers.length !== 1 ? 's' : ''}
              </CardDescription>
            </div>
            <Button
              size="sm"
              onClick={() => {
                setEditingCustomer(null)
                setCustomerDialogOpen(true)
              }}
              className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-kaycoo-teal-dark border-t-transparent" />
            </div>
          ) : customers.length === 0 ? (
            <Alert>
              <AlertDescription className="text-sm">
                No customers yet. Click + to add your first customer.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-2">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  className={`group cursor-pointer rounded-lg border p-3 transition-colors ${
                    selectedCustomerId === customer.id
                      ? 'border-kaycoo-teal-dark bg-kaycoo-teal-pale'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedCustomerId(customer.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{customer.name}</p>
                      {customer.company_code && (
                        <p className="text-xs text-gray-500">{customer.company_code}</p>
                      )}
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingCustomer(customer.id)
                          setCustomerDialogOpen(true)
                        }}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-red-500"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteCustomer(customer.id)
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Entities & Users for Selected Customer */}
      <div className="space-y-6 lg:col-span-2">
        {selectedCustomer ? (
          <>
            {/* Header */}
            <Card>
              <CardHeader>
                <CardTitle>{selectedCustomer.name}</CardTitle>
                <CardDescription>
                  {selectedCustomer.contact_email && `${selectedCustomer.contact_email} • `}
                  {entities.length} entit{entities.length !== 1 ? 'ies' : 'y'} • {users.length} user{users.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Entities */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Server className="h-4 w-4" />
                    D365 Entities
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={() => setEntityDialogOpen(true)}
                    className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Entity
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {entities.length === 0 ? (
                  <Alert>
                    <AlertDescription className="text-sm">
                      No entities yet. Add a D365 instance for this customer.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-2">
                    {entities.map((entity) => (
                      <div key={entity.id} className="flex items-center justify-between rounded-lg border p-3">
                        <div>
                          <p className="font-medium">{entity.name}</p>
                          <div className="flex gap-2 text-xs text-gray-500">
                            {entity.d365_version && <span>v{entity.d365_version}</span>}
                            {entity.environment_type && <span className="capitalize">{entity.environment_type}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Users */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <UsersIcon className="h-4 w-4" />
                    End Users
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={() => setUserDialogOpen(true)}
                    className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {users.length === 0 ? (
                  <Alert>
                    <AlertDescription className="text-sm">
                      No users yet. Add end users who report issues.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-2">
                    {users.map((user) => (
                      <div key={user.id} className="flex items-center justify-between rounded-lg border p-3">
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <div className="flex gap-2 text-xs text-gray-500">
                            {user.email && <span>{user.email}</span>}
                            {user.job_title && <span>• {user.job_title}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Customer Documents */}
            {selectedCustomerId && <CustomerDocumentsSection customerId={selectedCustomerId} />}
          </>
        ) : (
          <Card>
            <CardContent className="flex items-center justify-center py-12 text-center">
              <div>
                <Building2 className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                <p className="text-sm text-gray-500">Select a customer to view details</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialogs */}
      <CustomerDialog
        open={customerDialogOpen}
        onOpenChange={setCustomerDialogOpen}
        customerId={editingCustomer}
      />
      <EntityDialog
        open={entityDialogOpen}
        onOpenChange={setEntityDialogOpen}
        customerId={selectedCustomerId}
      />
      <CustomerUserDialog
        open={userDialogOpen}
        onOpenChange={setUserDialogOpen}
        customerId={selectedCustomerId}
        entities={entities}
      />
    </div>
  )
}
